import { useState } from 'react';
import { ShoppingCart, Menu, X, UserCircle, LogIn, ShieldCheck, MessageSquareHeart } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePricingConfig } from '../hooks/usePricingConfig';
import { formatCentsUSD } from '../lib/money';
import logoCircle from '../assets/logo-circle.webp';

export function Navigation({ cartCount }: { cartCount: number }) {
  const [isOpen, setIsOpen] = useState(false);
  const { user, loading } = useAuth();
  const { pathname } = useLocation();

  // The free-shipping threshold comes from the store_settings DB row (via the
  // public pricing-config endpoint). Until it loads — and unless free shipping
  // is actually enabled (non-null threshold) — no banner is shown.
  const pricing = usePricingConfig();
  // While online ordering is off (retail-only "Coming Soon" mode) the cart and
  // free-shipping banner are hidden; treat "still loading" the same way so a
  // buy affordance never flashes and then disappears.
  const orderingEnabled = pricing?.onlineOrderingEnabled ?? false;
  const freeShippingThresholdCentsUSD = orderingEnabled ? (pricing?.freeShippingThresholdCentsUSD ?? null) : null;

  // On the home page the nav overlays the full-bleed hero with white text;
  // on every other route it stays a solid, sticky white bar.
  const overlay = pathname === '/';

  const links = [
    { to: '/', label: 'Home' },
    { to: '/about', label: 'About' },
    { to: '/products', label: 'Shop' },
    { to: '/locations', label: 'Where to Find' },
  ];

  const isLoggedIn = !loading && !!user;
  const isGuest = !loading && !user;

  const linkClass = overlay
    ? 'text-white/90 hover:text-white transition-colors'
    : 'text-gray-700 hover:text-coastal-600 transition-colors';
  const iconClass = overlay
    ? 'text-white/90 hover:text-white transition-colors'
    : 'text-gray-700 hover:text-coastal-600 transition-colors';

  return (
    <header className={overlay ? 'absolute inset-x-0 top-0 z-50' : 'sticky top-0 z-50'}>
      {freeShippingThresholdCentsUSD != null && (
        <div className="bg-coastal-800 text-white text-center text-xs sm:text-sm font-medium py-2 px-4">
          Free shipping on all orders over {formatCentsUSD(freeShippingThresholdCentsUSD)}!
        </div>
      )}
      <nav className={overlay ? 'bg-transparent' : 'bg-white shadow-md'}>
        <div className="container-max flex justify-between items-center h-20">
          <Link to="/" className="flex items-center gap-2">
            <img src={logoCircle} alt="Coastal Harvest Co." className="w-14 h-14" />
            <span className="font-serif text-xl tracking-wide">
              <span className={overlay ? 'font-bold text-white' : 'font-bold text-coastal-600'}>Coastal Harvest</span>
              <span className={overlay ? 'text-white/80 ml-1 text-sm font-bold' : 'text-gold-500 ml-1 text-sm font-bold'}>Co.</span>
            </span>
          </Link>

          <div className="hidden md:flex gap-8">
            {links.map(({ to, label }) => (
              <Link key={to} to={to} className={linkClass}>
                {label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-4">
            {isLoggedIn && user!.isAdmin && (
              <Link to="/admin" className={`hidden md:flex items-center gap-1 text-sm ${linkClass}`}>
                <ShieldCheck className="w-5 h-5" />
                <span>Admin</span>
              </Link>
            )}

            {isLoggedIn && (
              <Link to="/account" className={`hidden md:flex items-center gap-1 text-sm ${linkClass}`}>
                <UserCircle className="w-5 h-5" />
                <span>{user!.name.split(' ')[0]}</span>
              </Link>
            )}

            {isGuest && (
              <Link to="/account" className={`hidden md:flex items-center gap-1 text-sm ${linkClass}`}>
                <LogIn className="w-5 h-5" />
                <span>Sign In</span>
              </Link>
            )}

            {orderingEnabled && (
              <Link to="/cart" className="relative">
                <ShoppingCart className={`w-6 h-6 ${iconClass}`} />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-teal-400 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Link>
            )}

            <button className={`md:hidden ${iconClass}`} onClick={() => setIsOpen(!isOpen)} aria-label="Toggle menu">
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {isOpen && (
          <div className={`md:hidden px-4 py-4 flex flex-col gap-4 ${overlay ? 'bg-coastal-800/95 backdrop-blur-sm' : 'bg-gray-50'}`}>
            {links.map(({ to, label }) => (
              <Link key={to} to={to} className={linkClass} onClick={() => setIsOpen(false)}>
                {label}
              </Link>
            ))}
            <Link to="/feedback" className={`flex items-center gap-2 ${linkClass}`} onClick={() => setIsOpen(false)}>
              <MessageSquareHeart className="w-5 h-5" /> Leave Feedback
            </Link>
            {isLoggedIn && user!.isAdmin && (
              <Link to="/admin" className={`flex items-center gap-2 ${linkClass}`} onClick={() => setIsOpen(false)}>
                <ShieldCheck className="w-5 h-5" /> Admin
              </Link>
            )}
            {isLoggedIn && (
              <Link to="/account" className={`flex items-center gap-2 ${linkClass}`} onClick={() => setIsOpen(false)}>
                <UserCircle className="w-5 h-5" /> My Account
              </Link>
            )}
            {isGuest && (
              <Link to="/account" className={`flex items-center gap-2 ${linkClass}`} onClick={() => setIsOpen(false)}>
                <LogIn className="w-5 h-5" /> Sign In
              </Link>
            )}
          </div>
        )}
      </nav>
    </header>
  );
}
